import { useLazyQuery } from '@apollo/client'
import { useNavigate } from '@faceup/router'
import { useContext, useEffect, useRef } from 'react'
import { graphql } from '../__generated__'
import { LayoutContext } from '../Contexts/LayoutContext'
import { UserContext } from '../Contexts/UserContext'
import useRegion from '../utils/useRegion'

const query = graphql(`
  query ChannelRedirectQuery($id: GraphQLString!) {
    publicReportSourceByShortId(id: $id) {
      password
      reportSource {
        id
        sourceType
        institution {
          id
        }
      }
    }
  }
`)

const channelShortIdPattern = /^\/c\/([a-zA-Z0-9_]+)/

/**
 * Handles a `data-redirect` target coming from a page's content.
 *
 * On the web the report source follows the URL, so a plain navigation resolves
 * the target channel. In the native app (`client === 'app'`) the report source
 * is pinned to the logged-in channel and the URL shortId is ignored — so
 * navigating to a *different* channel's index bounces back to the current
 * channel's default page, which re-issues the redirect, causing an infinite
 * loop. To support cross-channel redirects in the app we resolve the target
 * channel and switch the saved report source to it before navigating.
 */
export const useChannelRedirect = (redirectTarget: string | undefined) => {
  const { client } = useContext(LayoutContext)
  const { reportSource, setReportSource } = useContext(UserContext)
  const navigate = useNavigate()
  const { discoverByShortlink } = useRegion()
  const [fetchChannel] = useLazyQuery(query, { fetchPolicy: 'cache-and-network' })
  const hasRedirected = useRef(false)

  const isApp = client === 'app'

  // Only allow same-origin relative paths to prevent open-redirect / unsafe-scheme attacks.
  const safeTarget =
    redirectTarget?.startsWith('/') && !redirectTarget.startsWith('//') ? redirectTarget : undefined

  const targetShortId = safeTarget?.match(channelShortIdPattern)?.[1]

  // Same channel (or not a `/c/:shortId` link): switching nothing would only
  // loop, so in the app we leave the page as-is rather than redirecting.
  const isCrossChannelInApp = Boolean(
    isApp && targetShortId && targetShortId !== reportSource?.shortLink
  )

  useEffect(() => {
    if (!safeTarget || hasRedirected.current) {
      return
    }

    if (!isApp) {
      hasRedirected.current = true
      window.location.assign(safeTarget)
      return
    }

    if (!isCrossChannelInApp || !targetShortId) {
      return
    }

    const switchChannel = async () => {
      hasRedirected.current = true
      const discovery = await discoverByShortlink(targetShortId)
      const { data } = await fetchChannel({ variables: { id: targetShortId } })
      const channel = data?.publicReportSourceByShortId

      if (!discovery?.reportSourceId || !discovery.type || !channel) {
        hasRedirected.current = false
        return
      }

      setReportSource({
        channelType: channel.reportSource.sourceType,
        defaultFormId: discovery.reportSourceId,
        institutionId: channel.reportSource.institution.id,
        type: discovery.type,
        shortLink: targetShortId,
        ...(channel.password && { password: channel.password }),
      })
      navigate(routes => routes.page({ shortId: targetShortId }))
    }

    switchChannel()
  }, [
    safeTarget,
    targetShortId,
    isApp,
    isCrossChannelInApp,
    discoverByShortlink,
    fetchChannel,
    setReportSource,
    navigate,
  ])

  return { isRedirecting: Boolean(safeTarget) && (!isApp || isCrossChannelInApp) }
}
